import { createContext, useContext, useEffect, useMemo, useRef } from 'react';

const FrameStateContext = createContext<{
  authHandledByParent: boolean;
  instanceId: string | null;
}>({
  authHandledByParent: false,
  instanceId: null,
});

export const FrameStateProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // Pull authHandledByParent and instanceId from the URL search params
  const authHandledByParent = useRef(
    new URLSearchParams(window.location.search).get('authHandledByParent') ===
      'true',
  );
  const instanceId = useRef(
    new URLSearchParams(window.location.search).get('instanceId'),
  );

  // Correct window.history. The internal router is corrected by asl/web/apps/client/src/hooks/iframe/useParentRouteHandler.tsx
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (authHandledByParent.current) {
      params.delete('authHandledByParent');
      params.delete('instanceId');
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}?${params.toString()}`,
      );
    }
  }, []);

  const value = useMemo(
    () => ({
      authHandledByParent: authHandledByParent.current,
      instanceId: instanceId.current,
    }),
    [authHandledByParent, instanceId],
  );

  return (
    <FrameStateContext.Provider value={value}>
      {children}
    </FrameStateContext.Provider>
  );
};

export const useFrameState = () => {
  const { authHandledByParent, instanceId } = useContext(FrameStateContext);
  return { authHandledByParent, instanceId };
};
